import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Trash2, 
  Loader2,
  MessageCircle,
  Lock
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { deleteSavedAccount } from '@/lib/saved-accounts-service';
import { Badge } from '@/components/ui/badge';
import { useAccountQuota } from '@/hooks/use-account-quota';

interface SavedAccount {
  id: string;
  username: string;
  profile_data?: {
    profilePicUrl?: string;
    fullName?: string;
    biography?: string;
    followersCount?: number;
    postsCount?: number;
  };
}

interface SavedAccountsGridProps {
  accounts: SavedAccount[];
  onAccountsChanged?: () => void;
}

export function SavedAccountsGrid({ accounts, onAccountsChanged }: SavedAccountsGridProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: quotaInfo } = useAccountQuota();
  const [accountToDelete, setAccountToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Check if user has a paid subscription (Basic or Premium)
  const hasPaidSubscription = quotaInfo?.subscription_tier === 'basic' || quotaInfo?.subscription_tier === 'premium';

  const handleChatSelect = (username: string) => {
    navigate('/', { state: { username } });
  };
  
  const handleDelete = async (accountId: string) => {
    // Prevent deletion for free users
    if (!hasPaidSubscription) {
      toast({
        title: "구독 필요",
        description: "계정 삭제는 베이직 플랜 이상 구독자만 가능합니다.",
        variant: "destructive",
      });
      return;
    }
    
    try {
      setIsDeleting(true);
      const result = await deleteSavedAccount(accountId);
      
      if (result.success) {
        toast({
          title: "계정 삭제 완료",
          description: `계정이 성공적으로 삭제되었습니다.`,
        });
        
        // Call the callback to refresh the parent component
        if (onAccountsChanged) {
          onAccountsChanged();
        }
      } else {
        toast({
          title: "계정 삭제 실패",
          description: result.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      toast({
        title: "오류",
        description: "계정을 삭제하는 중 오류가 발생했습니다.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setAccountToDelete(null);
    }
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>, account: SavedAccount) => {
    console.log(`이미지 로드 실패: ${account.username}`);
    const target = e.target as HTMLImageElement;
    target.src = '/default-profile.png';
    target.onerror = null; // 무한 루프 방지
    
    // 프로필 데이터에 이미지 URL 업데이트 (선택 사항)
    if (account.profile_data) {
      account.profile_data.profilePicUrl = '/default-profile.png';
    }
  };

  if (accounts.length === 0) {
    return (
      <Card className="overflow-hidden border shadow-md">
        <div className="flex flex-col items-center justify-center p-10 text-center">
          <div className="rounded-full bg-primary/10 p-4 mb-4">
            <MessageCircle className="h-8 w-8 text-primary" />
          </div>
          <CardTitle className="mb-2">저장된 계정이 없습니다</CardTitle>
          <CardDescription className="mb-6 max-w-sm">
            인스타그램 계정을 추가하고 대화를 시작하세요.
          </CardDescription>
          <Button onClick={() => navigate('/')} className="gradient-button">
            계정 추가하기
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {accounts.map((account) => (
        <Card 
          key={account.id} 
          className="overflow-hidden border bg-card/60 backdrop-blur-sm transition-all hover:shadow-lg hover:translate-y-[-3px] duration-300"
        >
          <CardHeader className="pb-2">
            <div className="flex items-center gap-3">
              <Avatar className="h-12 w-12 ring-2 ring-primary/10">
                <AvatarImage
                  src={account.profile_data?.profilePicUrl}
                  alt={account.username}
                  onError={(e) => handleImageError(e, account)}
                />
                <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white">
                  {account.username.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div>
                <CardTitle className="text-base">
                  <span className="font-normal text-muted-foreground">@</span>{account.username}
                </CardTitle>
                {account.profile_data?.fullName && (
                  <CardDescription className="truncate max-w-[150px]">
                    {account.profile_data.fullName}
                  </CardDescription>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="pb-3">
            <div className="flex gap-2 mb-3">
              {account.profile_data?.followersCount && (
                <Badge variant="outline" className="px-2 h-6">
                  팔로워 {new Intl.NumberFormat('ko-KR', {
                    notation: 'compact',
                    compactDisplay: 'short'
                  }).format(account.profile_data.followersCount)}
                </Badge>
              )}
              {account.profile_data?.postsCount && (
                <Badge variant="outline" className="px-2 h-6">
                  게시물 {new Intl.NumberFormat('ko-KR', {
                    notation: 'compact',
                    compactDisplay: 'short'
                  }).format(account.profile_data.postsCount)}
                </Badge>
              )}
            </div>
            {account.profile_data?.biography && (
              <p className="line-clamp-2 text-sm text-muted-foreground">
                {account.profile_data.biography}
              </p>
            )}
          </CardContent>
          <CardFooter className="flex gap-2 pt-3">
            <Button 
              className="flex-1 gradient-button"
              onClick={() => handleChatSelect(account.username)}
              size="sm"
            >
              <MessageCircle className="mr-2 h-4 w-4" />
              대화하기
            </Button>
            {hasPaidSubscription ? (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="shrink-0 text-destructive hover:bg-destructive/10 hover:text-destructive border-destructive/20"
                    onClick={(e) => {
                      e.stopPropagation();
                      setAccountToDelete(account.id);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>계정 삭제</AlertDialogTitle>
                    <AlertDialogDescription>
                      정말로 @{account.username} 계정을 삭제하시겠습니까? 
                      삭제하더라도 대화 기록은 보존됩니다.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>취소</AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      disabled={isDeleting}
                      onClick={() => handleDelete(account.id)}
                    >
                      {isDeleting && accountToDelete === account.id ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="mr-2 h-4 w-4" />
                      )}
                      삭제
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            ) : (
              <Button
                variant="outline"
                size="icon"
                className="shrink-0 text-muted-foreground hover:bg-muted/50 border-muted"
                onClick={() => {
                  toast({
                    title: "구독 필요",
                    description: "계정 삭제는 베이직 플랜 이상 구독자만 가능합니다.",
                    variant: "destructive",
                  });
                  navigate("/pricing");
                }}
              >
                <Lock className="h-4 w-4" />
              </Button>
            )}
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}